import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, Edit2, Trash2, TreeDeciduous } from 'lucide-react';
import { Button } from '../Button';
import { Card } from '../Card';
import { cn } from '../../../utils/cn';
import type { GrowingAreaCardProps } from './GrowingAreaCard.types';

// ============================================================
// GrowingAreaCard - Level 3 Domain Card
// Wraps Card (Level 1) with growing area layout.
// See: DESIGN_SYSTEM.md for architecture rules.
// ============================================================

// --- Area type labels ---
const areaTypeLabels: Record<string, string> = {
  tower: 'Věž',
  bed: 'Záhon',
  rack: 'Regál',
  nft: 'NFT',
  dwc: 'DWC',
  ebb_flow: 'Ebb & Flow',
  drip: 'Kapková',
  aeroponics: 'Aeroponika',
  propagation: 'Množení',
  soil: 'Půda',
  other: 'Ostatní',
};

const AREA_BORDER_CLASSES: Record<string, string> = {
  '#22c55e': 'border-l-green-500',
  '#06b6d4': 'border-l-cyan-500',
  '#14b8a6': 'border-l-teal-500',
  '#f59e0b': 'border-l-amber-500',
  '#f43f5e': 'border-l-rose-500',
  '#0f766e': 'border-l-teal-700',
  '#64748b': 'border-l-slate-500',
};

export const GrowingAreaCard: React.FC<GrowingAreaCardProps> = ({
  area,
  occupancy,
  onEdit,
  onDelete,
  onClick,
  className,
}) => {
  const navigate = useNavigate();

  const totalSlots = occupancy?.totalSlots || area.grid_rows * area.grid_cols || 0;
  const occupiedSlots = occupancy?.occupiedSlots || 0;
  const occupancyPercent = totalSlots > 0 ? Math.round((occupiedSlots / totalSlots) * 100) : 0;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/growing-areas/${area.id}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={className}
    >
      <Card
        variant="glass"
        padding="none"
        className={cn(
          'group overflow-hidden relative border-l-4 transition-all h-full',
          AREA_BORDER_CLASSES[(area.color || '#22c55e').toLowerCase()] || 'border-l-green-500',
        )}
      >
        {/* Background Decoration */}
        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
          <Layers size={100} />
        </div>

        <div className="p-6 cursor-pointer flex flex-col h-full" onClick={handleClick}>
          {/* Header: Type Badge + Actions */}
          <div className="flex justify-between items-start mb-4">
            <span className="px-2 py-1 bg-green-50 dark:bg-green-900/20 rounded text-xs font-mono font-bold text-green-600 dark:text-green-400 uppercase">
              {areaTypeLabels[area.type] || area.type}
            </span>

            {(onEdit || onDelete) && (
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                {onEdit && (
                  <Button
                    onClick={() => onEdit(area)}
                    variant="ghost"
                    className="p-1.5 text-gray-400 hover:text-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 transition-all h-auto w-auto"
                    title="Upravit"
                  >
                    <Edit2 size={16} />
                  </Button>
                )}
                {onDelete && (
                  <Button
                    onClick={() => onDelete(area.id)}
                    variant="ghost"
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all h-auto w-auto"
                    title="Smazat"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-green-500 transition-colors truncate">
            {area.name}
          </h3>

          {/* Tags Row */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs px-2 py-1 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 font-medium">
              <TreeDeciduous size={12} className="inline mr-1" />
              Pozice: {occupiedSlots} / {totalSlots}
            </span>
            <span className="text-xs px-2 py-1 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-medium">
              {area.location === 'indoor' ? '🏠 Doma' : '🌳 Venku'}
            </span>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-500 dark:text-zinc-400 mb-6 line-clamp-2 min-h-[40px] flex-grow">
            {area.description || 'Bez popisu'}
          </p>

          {/* Occupancy Bar */}
          <div className="pt-4 border-t border-gray-100 dark:border-white/5 mt-auto">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Obsazenost pozic</span>
              <span>{occupancyPercent}%</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-zinc-700 rounded-full overflow-hidden">
              <motion.div
                className={cn(
                  'h-full origin-left transition-all duration-500',
                  occupancyPercent > 80
                    ? 'bg-red-500'
                    : occupancyPercent > 50
                      ? 'bg-yellow-500'
                      : 'bg-green-500',
                )}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: occupancyPercent / 100 }}
              />
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default GrowingAreaCard;
